import React, { useState } from "react"
import NotFound from "client/parts/notFound"

type FunctionComponent = (props: any) => JSX.Element

export interface RouteProps {
    exact?: boolean
    path: string
    component: React.ElementType | FunctionComponent
}

export interface SwitchProps {
    children: JSX.Element[]
}

export interface ComponentProps {
    search?: string
    hash?: string
}

export interface LinkProps {
    href: string
    onClick: React.MouseEventHandler<HTMLAnchorElement>
    children: React.ReactNode
}

export const Link = ({
    href,
    onClick,
    children,
}: LinkProps) => (
    <a
        href={href}
        onClick={(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            e.preventDefault()
            onClick(e)
        }}
    >
        {children}
    </a>
)

// eslint-disable-next-line react/jsx-no-useless-fragment
export const Route = ({ exact, path, component }: RouteProps) => (exact || path || component) && <></>

export const Switch = ({ children }: SwitchProps) => {
    const [pathname] = useState(window.location.pathname)
    const urlArray = pathname.split("/")

    let functionComponent: FunctionComponent
    let Element: React.ElementType
    let componentProps: any = {}

    children.every((child: any) => {
        const { exact, path, component } = child.props as RouteProps

        if (exact) {
            if (path === pathname) {
                functionComponent = component as FunctionComponent
                Element = component as React.ElementType
                return false // break
            }
        } else {
            if (path === "/") {
                functionComponent = component as FunctionComponent
                Element = component as React.ElementType
                return false // break
            }

            const templateArray = path.split("/")
            if (urlArray.length < templateArray.length) {
                return true // continue
            }

            const minLength = Math.min(urlArray.length, templateArray.length)
            let match = 0
            componentProps = {
                search: window.location.search,
                hash: window.location.hash,
            }

            for (let i = 0; i < minLength; i++) {
                const urlItem = urlArray[i]
                const templateItem = templateArray[i]

                if (urlItem === templateItem) match += 1
                else if (templateItem.length > 1 && templateItem[0] === ":") {
                    let propertyName: string
                    const endIndex = templateItem.length - 1

                    if (templateItem[endIndex] === "*") {
                        propertyName = templateItem.substring(1, endIndex)
                        componentProps[propertyName] = urlArray.slice(i, urlArray.length).join("/")
                    } else {
                        propertyName = templateItem.substring(1)
                        componentProps[propertyName] = Number(urlItem) >= 0 ? Number(urlItem) : urlItem
                    }

                    match += 1
                } else {
                    break
                }
            }

            if (match === minLength) {
                functionComponent = component as FunctionComponent
                Element = component as React.ElementType
                return false // break
            }
        }
        return true // continue
    })

    if (functionComponent && typeof functionComponent === "function") return functionComponent(componentProps)
    if (Element) return <Element {...componentProps} />
    return <NotFound />
}
